import React, { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  Card,
  ErrorBoundary,
  Form,
  Menu,
  Page,
  QueryResult,
  Section,
  Toast,
} from "@sscs/ui";
import { SaveIcon } from "@sscs/ui/icons";
import { getQueryStatus } from "@sscs/utils";
import { PERMISSION } from "Constants";
import { useAllowedTo, useCurrentOrganization, useRedirect } from "Hooks";
import { apiErrorToast } from "Utils";
import {
  INACTIVITY_PERIOD_MINUTES_OPTIONS,
  SESSION_DURATION_MINUTES_OPTIONS,
  SESSION_POLICY_OPTIONS,
} from "../../constants";
import {
  useGetSessionConfigQuery,
  useUpdateSessionConfigMutation,
} from "../../services/session.api";
import {
  GetSessionConfigResponse,
  SessionConfigurationFormData,
} from "../../types";
import { SessionConfigurationFormSchema } from "../../utils";
import styles from "./sessionConfig.module.scss";

const SessionConfig = () => {
  const canConfigureSession = useAllowedTo({
    permissions: [PERMISSION.MODIFY_SESSION_CONFIGURATION],
  });
  useRedirect({
    to: "/page-not-found",
    when: !canConfigureSession,
  });

  const currentOrganization = useCurrentOrganization();

  const {
    data: sessionConfiguration = {
      persistent_session: true,
      max_session_duration_minutes: 20160,
      refresh_session_on_activity: true,
      enable_inactivity_timeout: false,
      inactivity_timeout_minutes: 60,
    } satisfies GetSessionConfigResponse,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetSessionConfigQuery(
    { organizationId: currentOrganization?.id || "" },
    { skip: !currentOrganization || !canConfigureSession },
  );

  const [updateSessionConfiguration] = useUpdateSessionConfigMutation();

  const formMethods = useForm<SessionConfigurationFormData>({
    resolver: yupResolver(SessionConfigurationFormSchema),
    mode: "onChange",
    values: {
      ...sessionConfiguration,
      persistent_session: sessionConfiguration.persistent_session.toString(),
    },
  });

  const {
    formState: { errors, isDirty, defaultValues },
    handleSubmit,
    setValue,
    watch,
    trigger,
    reset,
  } = formMethods;

  useEffect(() => {
    reset({
      ...sessionConfiguration,
      persistent_session: sessionConfiguration.persistent_session.toString(),
    });
  }, [reset, sessionConfiguration]);

  const onSubmit: SubmitHandler<SessionConfigurationFormData> = async (
    data,
  ) => {
    try {
      if (currentOrganization) {
        const updatedConfiguration = await updateSessionConfiguration({
          organizationId: currentOrganization?.id,
          body: {
            ...data,
            persistent_session: data.persistent_session === "true",
          },
        }).unwrap();

        Toast.show({
          title: "Session configuration updated",
          type: "success",
        });

        reset({
          ...updatedConfiguration,
          persistent_session:
            updatedConfiguration.persistent_session.toString(),
        });
      }
    } catch (error) {
      apiErrorToast(error, "Could not update session configuration");
    }
  };

  if (!canConfigureSession) return null;

  const result = (
    <Page.Root>
      <Page.Header
        title="Session lifetime configuration"
        actions={
          <Button
            type="submit"
            form="session-configuration-form"
            color="primary"
            disabled={!isDirty}
          >
            <SaveIcon />
            Save
          </Button>
        }
      />
      <Page.Body>
        <QueryResult
          status={getQueryStatus({
            isFetching,
            isLoading,
            isError,
            error,
            data: sessionConfiguration,
          })}
        >
          <FormProvider {...formMethods}>
            <Form.Form
              id="session-configuration-form"
              onSubmit={handleSubmit(onSubmit)}
              name="session-configuration-form"
            >
              <Card.Card>
                <Card.Content className={styles.sections}>
                  <Section>
                    <Section.Title as="h2">Session policy</Section.Title>
                    <Section.Content className={styles.fields}>
                      <Form.RadioGroup
                        id="session-policy"
                        name="persistent_session"
                        items={SESSION_POLICY_OPTIONS}
                      />
                    </Section.Content>
                  </Section>
                  <Section>
                    <Section.Title as="h2">Session duration</Section.Title>
                    <Section.Content className={styles.fields}>
                      <Form.DropdownMenu
                        id="max-session-duration"
                        name="max_session_duration_minutes"
                        label="Maximum session duration"
                        helperText="Select how long users can stay signed in before re-authentication is required"
                        onChange={() => {
                          trigger("inactivity_timeout_minutes");
                        }}
                      >
                        {SESSION_DURATION_MINUTES_OPTIONS.map(
                          ({ label, value }) => (
                            <Menu.Item
                              key={`${value}-duration-minutes`}
                              id={`${value}-duration-minutes`}
                              value={value}
                            >
                              {label}
                            </Menu.Item>
                          ),
                        )}
                      </Form.DropdownMenu>
                    </Section.Content>
                  </Section>
                  <Section>
                    <Section.Title as="h2">Session refresh</Section.Title>
                    <Section.Content className={styles.fields}>
                      <Form.Checkbox
                        id="refresh-session"
                        name="refresh_session_on_activity"
                        label={
                          <span className={styles["checkbox-label"]}>
                            Refresh session on activity
                          </span>
                        }
                        helperText="When enabled, the user’s session is refreshed whenever activity is detected"
                        className={styles["form-checkbox"]}
                      />
                    </Section.Content>
                  </Section>
                  <Section>
                    <Section.Title as="h2">
                      Session inactivity timeout
                    </Section.Title>
                    <Section.Content className={styles.fields}>
                      <Form.Checkbox
                        id="automatic-signout"
                        name="enable_inactivity_timeout"
                        label={
                          <span className={styles["checkbox-label"]}>
                            Apply automatic sign-out on inactivity
                          </span>
                        }
                        helperText="When enabled, users will be logged out after the specified time interval"
                        className={styles["form-checkbox"]}
                        onChange={(event) => {
                          if (event.target.checked === false) {
                            setValue(
                              "inactivity_timeout_minutes",
                              defaultValues?.inactivity_timeout_minutes ??
                                sessionConfiguration.inactivity_timeout_minutes,
                              { shouldDirty: true },
                            );
                          }
                          trigger("inactivity_timeout_minutes");
                        }}
                      />
                      <Form.DropdownMenu
                        id="signout-inactivity-period"
                        name="inactivity_timeout_minutes"
                        label="Inactivity before sign-out"
                        errors={errors}
                        isDisabled={!watch("enable_inactivity_timeout")}
                      >
                        {INACTIVITY_PERIOD_MINUTES_OPTIONS.map(
                          ({ label, value }) => (
                            <Menu.Item
                              key={`${value}-duration-minutes`}
                              id={`${value}-duration-minutes`}
                              value={value}
                            >
                              {label}
                            </Menu.Item>
                          ),
                        )}
                      </Form.DropdownMenu>
                    </Section.Content>
                  </Section>
                </Card.Content>
              </Card.Card>
            </Form.Form>
          </FormProvider>
        </QueryResult>
      </Page.Body>
    </Page.Root>
  );

  return <ErrorBoundary>{result}</ErrorBoundary>;
};

export default SessionConfig;
